import { useState, type MouseEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Check, Copy } from 'lucide-react';
import { useCopy } from '../../lib/copyToClipboard';
import {
  GET_DATA_SOURCES,
  GET_COWORK_MCP_CONNECTOR,
  CREATE_DATA_SOURCE,
  UPDATE_DATA_SOURCE,
  CREATE_COWORK_MCP_CONNECTOR,
  ROTATE_COWORK_MCP_CREDENTIALS,
} from '../../graphql';
import {
  GetDataSourcesResponse,
  GetCoworkMcpConnectorResponse,
  CoworkMcpCredentials,
} from '../../types/graphql';
import { DataSourceConfig } from './DataSourceConfig';
import { useAccount } from '../../context/AccountContext';
import { MCP_TOOLS_VERSION } from '../../config/mcpToolsVersion';
import { Loader } from '../common/PageLoader';

const COWORK_PARTNER_URL = 'partner://claude-cowork';

const AGENT_PERMS = [
  ['drafts', 'Create & edit drafts', 'Articles, media, fields'],
  ['pub', 'Publish & schedule', 'The copy edit must pass first'],
  ['surface', 'Change the site surface', 'Rules for each area are set in Site → Surface'],
  ['del', 'Delete content', 'Unpublishing is always allowed'],
] as const;

const PERM_CHOICES = ['Allow', 'Ask first', 'Never'] as const;

function sourceKind(type: string) {
  switch (type?.toLowerCase()) {
    case 'rss':
      return 'RSS feed';
    case 'mrss':
      return 'Media RSS';
    case 'youtube':
      return 'YouTube';
    case 'wordpress':
      return 'WordPress';
    case 'api_partner':
      return 'API partner';
    case 'browser_capture':
    case 'browser':
    case 'capture':
      return 'Browser extension';
    default:
      return type || 'Source';
  }
}

function sinceLabel(iso?: string) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `since ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

function lastActive(iso?: string | null) {
  if (!iso) return 'not used yet';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'not used yet';
  const now = new Date();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `last active ${time}`;
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `last active ${day}`;
}

function mark(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'AG').slice(0, 2).toUpperCase();
}

/**
 * Assistants cache the tool list when the connector is added, so a feature
 * shipped since then stays invisible until the person refreshes it. Collapsed
 * by default: the note is there when someone needs it and quiet otherwise.
 */
function ToolsVersionNote() {
  return (
    <div className="ink-conn-tools">
      <span className="ink-conn-tools-version">Tools version {MCP_TOOLS_VERSION}</span>
      <details className="ink-conn-refresh">
        <summary>Not seeing a recent feature?</summary>
        <div className="ink-conn-refresh-body">
          <p>Your assistant keeps a copy of what this connection can do. Ask it to look again:</p>
          <ul>
            <li>
              <strong>Claude (claude.ai):</strong> Settings → Connectors → this connector&apos;s ⋯
              menu → Refresh tools list, then start a new chat.
            </li>
            <li>
              <strong>Claude Code:</strong> start a new session, or reconnect the server.
            </li>
            <li>
              <strong>Other assistants (such as Grok):</strong> remove and re-add the connector,
              then start a new chat.
            </li>
          </ul>
          <p>Your content and approvals are not affected.</p>
        </div>
      </details>
    </div>
  );
}

interface NewDataSource {
  name: string;
  type: string;
  url: string;
}

export function IntegrationsSettings() {
  const { copy, state: copyState } = useCopy();
  // A credential copy button: copies the field beside it; if the browser
  // refuses, selects that field so ⌘C / Ctrl+C still works.
  const copyField = async (key: string, value: string, e: MouseEvent<HTMLElement>) => {
    const field = e.currentTarget.parentElement?.querySelector('input');
    if (!(await copy(key, value, e))) field?.select();
  };
  const copyIcon = (key: string) =>
    copyState(key) === 'copied' ? <Check className="size-4" /> : <Copy className="size-4" />;
  const [openDataSourceDialog, setOpenDataSourceDialog] = useState<boolean>(false);
  const [openCoworkCreateDialog, setOpenCoworkCreateDialog] = useState(false);
  const [openRotateConfirm, setOpenRotateConfirm] = useState(false);
  const [coworkLabel, setCoworkLabel] = useState('Claude Cowork');
  const [revealedCredentials, setRevealedCredentials] = useState<CoworkMcpCredentials | null>(null);
  const [coworkError, setCoworkError] = useState<string | null>(null);
  const [expandedDataSourceId, setExpandedDataSourceId] = useState<string | null>(null);
  const [agentPerms, setAgentPerms] = useState({ drafts: 0, pub: 1, surface: 1, del: 2 });
  const [newDataSource, setNewDataSource] = useState<NewDataSource>({
    name: '',
    type: '',
    url: '',
  });

  const { accountId, isLoading: accountsLoading } = useAccount();

  // Get data sources
  const {
    data: dataSourcesData,
    loading: dataSourcesLoading,
    refetch: refetchDataSources,
  } = useQuery<GetDataSourcesResponse>(GET_DATA_SOURCES, {
    variables: { accountId },
    skip: !accountId || accountsLoading,
  });

  const {
    data: coworkData,
    loading: coworkLoading,
    refetch: refetchCowork,
  } = useQuery<GetCoworkMcpConnectorResponse>(GET_COWORK_MCP_CONNECTOR, {
    variables: { accountId },
    skip: !accountId || accountsLoading,
  });

  const coworkConnector = coworkData?.coworkMcpConnector ?? null;

  const [createDataSource, { loading: creatingDataSource }] = useMutation(CREATE_DATA_SOURCE, {
    onCompleted: () => {
      setOpenDataSourceDialog(false);
      setNewDataSource({ name: '', type: '', url: '' });
      refetchDataSources();
    },
    onError: (error) => {
      console.error('Error creating data source:', error);
    },
  });

  const [updateDataSource, { loading: updatingDataSource }] = useMutation(UPDATE_DATA_SOURCE, {
    onCompleted: () => {
      refetchDataSources();
      refetchCowork();
    },
    onError: (error) => {
      console.error('Error updating data source:', error);
    },
  });

  const [createCoworkConnector, { loading: creatingCowork }] = useMutation(
    CREATE_COWORK_MCP_CONNECTOR,
    {
      onCompleted: (data) => {
        setOpenCoworkCreateDialog(false);
        setCoworkError(null);
        setRevealedCredentials(data.createCoworkMcpConnector);
        refetchCowork();
        refetchDataSources();
      },
      onError: (error) => {
        setCoworkError(error.message);
      },
    }
  );

  const [rotateCoworkCredentials, { loading: rotatingCowork }] = useMutation(
    ROTATE_COWORK_MCP_CREDENTIALS,
    {
      onCompleted: (data) => {
        setOpenRotateConfirm(false);
        setCoworkError(null);
        setRevealedCredentials(data.rotateCoworkMcpCredentials);
        refetchCowork();
      },
      onError: (error) => {
        setCoworkError(error.message);
      },
    }
  );

  const handleCreateDataSource = async () => {
    if (!newDataSource.name || !newDataSource.type || !newDataSource.url || !accountId) {
      return;
    }

    try {
      await createDataSource({
        variables: {
          input: {
            accountId,
            ...newDataSource,
          },
        },
      });
    } catch (error) {
      console.error('Error creating data source:', error);
    }
  };

  const handleCreateCowork = async () => {
    if (!accountId) return;
    setCoworkError(null);
    await createCoworkConnector({
      variables: { accountId, label: coworkLabel.trim() || 'Claude Cowork' },
    });
  };

  const handleRotateCowork = async () => {
    if (!accountId) return;
    setCoworkError(null);
    await rotateCoworkCredentials({
      variables: { accountId, label: coworkConnector?.label || 'Claude Cowork MCP' },
    });
  };

  const feedDataSources =
    dataSourcesData?.dataSources?.filter(
      (source) =>
        !(source.type?.toLowerCase() === 'api_partner' && source.url === COWORK_PARTNER_URL)
    ) ?? [];

  const wordpressSources = feedDataSources.filter(
    (source) => source.type?.toLowerCase() === 'wordpress'
  );
  const otherSources = feedDataSources.filter(
    (source) => source.type?.toLowerCase() !== 'wordpress'
  );

  return (
    <div>
      <h1 className="ink-h1">Connections</h1>
      <p className="ink-lede">
        The agents and sources that can read from and write to this account.
      </p>

      <div className="ink-conn-head">
        <h2>Agents</h2>
        <Dialog
          open={openCoworkCreateDialog}
          onOpenChange={(open) => {
            setOpenCoworkCreateDialog(open);
            if (!open) setCoworkError(null);
          }}
        >
          <DialogTrigger asChild>
            <button type="button" className="ink-btn ink-btn-primary" disabled={!accountId}>
              Connect an agent
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Connect Claude Cowork</DialogTitle>
              <DialogDescription>
                Creates an API partner source and issues MCP OAuth credentials. Copy the Client ID
                and Secret into Claude&apos;s Advanced connector settings.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cowork-label">Label</Label>
                <Input
                  id="cowork-label"
                  placeholder="Claude Cowork"
                  value={coworkLabel}
                  onChange={(e) => setCoworkLabel(e.target.value)}
                />
              </div>
              {coworkError && <p className="text-sm text-red-400">{coworkError}</p>}
            </div>
            <DialogFooter>
              <Button
                className="bg-button-secondary text-button-secondary"
                onClick={() => setOpenCoworkCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateCowork}
                disabled={creatingCowork || !accountId}
                className="bg-button-primary text-button-primary"
              >
                {creatingCowork ? 'Creating...' : 'Create & issue credentials'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {coworkLoading ? (
        <Loader label="Loading" className="ink-lede" />
      ) : coworkConnector ? (
        <div className="ink-conn-agent">
          <div className="ink-conn-agent-top">
            <span className="ink-conn-mark">{mark(coworkConnector.name || 'Agent')}</span>
            <div>
              <div className="ink-conn-name">
                {coworkConnector.name}
                {coworkConnector.hasCredentials && coworkConnector.isActive ? (
                  <span className="ink-conn-on">
                    <span
                      className="ink-dot"
                      style={{ background: 'var(--ink-green)', width: 6, height: 6 }}
                    />
                    Connected
                  </span>
                ) : (
                  <span className="ink-conn-on" style={{ color: 'var(--ink-ink3)' }}>
                    {coworkConnector.hasCredentials ? 'Paused' : 'No credentials'}
                  </span>
                )}
              </div>
              <div className="ink-conn-meta">
                Remote MCP · {lastActive(coworkConnector.lastUsedAt)}
              </div>
            </div>
            <div className="ink-conn-actions">
              <button
                type="button"
                className="ink-btn"
                onClick={(e) => copy('mcpUrl', coworkConnector.mcpUrl, e)}
                aria-live="polite"
              >
                {copyState('mcpUrl') === 'copied'
                  ? 'Copied'
                  : copyState('mcpUrl') === 'failed'
                    ? 'Couldn’t copy'
                    : 'Copy MCP URL'}
              </button>
              <button
                type="button"
                className="ink-btn"
                onClick={() => {
                  setCoworkError(null);
                  setOpenRotateConfirm(true);
                }}
                disabled={rotatingCowork}
              >
                Rotate secret
              </button>
              <button
                type="button"
                className="ink-bill-link"
                onClick={() =>
                  updateDataSource({
                    variables: {
                      id: coworkConnector.dataSourceId,
                      input: { isActive: !coworkConnector.isActive },
                    },
                  })
                }
                disabled={updatingDataSource}
              >
                {coworkConnector.isActive ? 'Pause' : 'Turn on'}
              </button>
            </div>
          </div>
          <div className="ink-conn-perms">
            {AGENT_PERMS.map(([key, name, desc]) => (
              <div className="ink-conn-perm" key={key}>
                <div>
                  <div className="ink-conn-perm-name">{name}</div>
                  <div className="ink-conn-perm-desc">{desc}</div>
                </div>
                <div className="ink-seg" style={{ fontSize: 12.5 }}>
                  {PERM_CHOICES.map((choice, index) => (
                    <button
                      key={choice}
                      type="button"
                      data-active={agentPerms[key] === index ? 'true' : 'false'}
                      onClick={() => setAgentPerms((prev) => ({ ...prev, [key]: index }))}
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {coworkError ? <p className="ink-lede">{coworkError}</p> : null}
          <ToolsVersionNote />
        </div>
      ) : (
        <p className="ink-lede" style={{ marginTop: 16 }}>
          No agent connected.
        </p>
      )}

      {/* Rotate confirmation */}
      <Dialog open={openRotateConfirm} onOpenChange={setOpenRotateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rotate MCP credentials?</DialogTitle>
            <DialogDescription>
              This revokes the current Client ID and Secret and issues a new pair. Update the values
              in Claude&apos;s Advanced connector settings immediately. The previous credentials
              will stop working.
            </DialogDescription>
          </DialogHeader>
          {coworkError && <p className="text-sm text-red-400">{coworkError}</p>}
          <DialogFooter>
            <Button
              className="bg-button-secondary text-button-secondary"
              onClick={() => setOpenRotateConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRotateCowork}
              disabled={rotatingCowork}
              className="bg-button-primary text-button-primary"
            >
              {rotatingCowork ? 'Rotating...' : 'Rotate & reveal new credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-shot credential reveal */}
      <Dialog
        open={Boolean(revealedCredentials)}
        onOpenChange={(open) => {
          if (!open) setRevealedCredentials(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>MCP credentials issued</DialogTitle>
            <DialogDescription>
              Copy these now. The full Client ID and Secret are not shown again — only the Client ID
              prefix remains visible. Use Rotate to recycle credentials later.
            </DialogDescription>
          </DialogHeader>
          {revealedCredentials && (
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>MCP URL</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={revealedCredentials.mcpUrl}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => copyField('cred-url', revealedCredentials.mcpUrl, e)}
                    aria-label={copyState('cred-url') === 'copied' ? 'Copied' : 'Copy'}
                    title={copyState('cred-url') === 'failed' ? 'Couldn’t copy — press ⌘C' : 'Copy'}
                  >
                    {copyIcon('cred-url')}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>OAuth Client ID</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={revealedCredentials.clientId}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => copyField('cred-id', revealedCredentials.clientId, e)}
                    aria-label={copyState('cred-id') === 'copied' ? 'Copied' : 'Copy'}
                    title={copyState('cred-id') === 'failed' ? 'Couldn’t copy — press ⌘C' : 'Copy'}
                  >
                    {copyIcon('cred-id')}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label>OAuth Client Secret</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={revealedCredentials.clientSecret}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => copyField('cred-secret', revealedCredentials.clientSecret, e)}
                    aria-label={copyState('cred-secret') === 'copied' ? 'Copied' : 'Copy'}
                    title={
                      copyState('cred-secret') === 'failed' ? 'Couldn’t copy — press ⌘C' : 'Copy'
                    }
                  >
                    {copyIcon('cred-secret')}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-amber-400">
                Paste Client ID and Secret under Claude → Customize → Connectors → Advanced
                settings, then Connect and Allow.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              className="bg-button-primary text-button-primary"
              onClick={() => setRevealedCredentials(null)}
            >
              Done — I saved them
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="ink-conn-head">
        <h2>Sources</h2>
        <Dialog open={openDataSourceDialog} onOpenChange={setOpenDataSourceDialog}>
          <DialogTrigger asChild>
            <button type="button" className="ink-bill-link">
              Add source
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New source</DialogTitle>
              <DialogDescription>Connect a feed to import content automatically.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="data-source-name">Name</Label>
                <Input
                  id="data-source-name"
                  placeholder="My RSS Feed"
                  value={newDataSource.name}
                  onChange={(e) => setNewDataSource((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data-source-type">Type</Label>
                <Select
                  value={newDataSource.type}
                  onValueChange={(value) => setNewDataSource((prev) => ({ ...prev, type: value }))}
                >
                  <SelectTrigger id="data-source-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rss">RSS Feed</SelectItem>
                    <SelectItem value="mrss">Media RSS Feed</SelectItem>
                    <SelectItem value="youtube">YouTube Channel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="data-source-url">URL</Label>
                <Input
                  id="data-source-url"
                  type="url"
                  placeholder="https://"
                  value={newDataSource.url}
                  onChange={(e) => setNewDataSource((prev) => ({ ...prev, url: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                className="bg-button-secondary text-button-secondary"
                onClick={() => setOpenDataSourceDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateDataSource}
                disabled={
                  !newDataSource.name ||
                  !newDataSource.type ||
                  !newDataSource.url ||
                  creatingDataSource
                }
                className="bg-button-primary text-button-primary"
              >
                {creatingDataSource ? 'Creating...' : 'Create source'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {dataSourcesLoading ? (
        <Loader label="Loading" className="ink-lede" />
      ) : otherSources.length === 0 ? (
        <p className="ink-lede" style={{ marginTop: 16 }}>
          No sources yet.
        </p>
      ) : (
        <div className="ink-conn-sources">
          {otherSources.map((source) => (
            <div className="ink-conn-source" key={source.id}>
              <div className="ink-conn-source-name">{source.name}</div>
              <div className="ink-conn-source-meta">
                {sourceKind(source.type)}
                {sinceLabel(source.createdAt) ? ` · ${sinceLabel(source.createdAt)}` : ''}
                {source.isActive ? '' : ' · Paused'}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
                <button
                  type="button"
                  className="ink-bill-link"
                  disabled={updatingDataSource}
                  onClick={() =>
                    updateDataSource({
                      variables: { id: source.id, input: { isActive: !source.isActive } },
                    })
                  }
                >
                  {source.isActive ? 'Pause' : 'Turn on'}
                </button>
                {['rss', 'mrss'].includes(source.type?.toLowerCase()) ? (
                  <button
                    type="button"
                    className="ink-bill-link"
                    onClick={() =>
                      setExpandedDataSourceId(expandedDataSourceId === source.id ? null : source.id)
                    }
                  >
                    {expandedDataSourceId === source.id ? 'Hide setup' : 'Configure'}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
      {expandedDataSourceId &&
      accountId &&
      otherSources.some((source) => source.id === expandedDataSourceId) ? (
        <div style={{ marginTop: 16 }}>
          <DataSourceConfig
            key={expandedDataSourceId}
            dataSourceId={expandedDataSourceId}
            accountId={accountId}
          />
        </div>
      ) : null}

      <div className="ink-conn-head">
        <h2>WordPress</h2>
        <span style={{ font: '500 13.5px/1 var(--ink-font)', color: 'var(--ink-accent)' }}>
          Connect a site
        </span>
      </div>
      {wordpressSources.length === 0 ? (
        <div className="ink-conn-note">
          Install the TinyCMS plugin, then pair it from WordPress to publish into an existing site
          instead of, or alongside, your public site.
        </div>
      ) : (
        <div className="ink-conn-sources">
          {wordpressSources.map((source) => (
            <div className="ink-conn-source" key={source.id}>
              <div className="ink-conn-source-name">{source.name}</div>
              <div className="ink-conn-source-meta">
                {source.url}
                {sinceLabel(source.createdAt) ? ` · ${sinceLabel(source.createdAt)}` : ''}
                {source.isActive ? '' : ' · Paused'}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ink-conn-head">
        <h2>API keys</h2>
      </div>
      <div className="ink-conn-keys">
        <div className="ink-bill-empty" style={{ padding: '15px 0' }}>
          No keys yet.
        </div>
      </div>
    </div>
  );
}
